import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useAtom } from 'jotai';
import { Provider } from 'react-redux';

import { DeleteQuestionEventAdapter } from '~analytics/adapters/DeleteQuestionEventAdapter';
import {
  TeacherApplicationContext,
  TeacherApplicationContextProvider,
} from '~shared/context/TeacherApplicationContext';
import { BulkQuestionsUrlProvider } from '~teacher/components/QuizEditor/contexts/BulkQuestionsUrlProvider';
import { DeleteQuizUrlContextProvider } from '~teacher/components/QuizEditor/contexts/DeleteQuizUrlContextProvider';
import { QuizQuestionOrderUrlProvider } from '~teacher/components/QuizEditor/contexts/QuizQuestionOrderUrlProvider';
import { QuizQuestionUrlProvider } from '~teacher/components/QuizEditor/contexts/QuizQuestionUrlProvider';
import { QuizUrlContextProvider } from '~teacher/components/QuizEditor/contexts/QuizUrlContextProvider';
import { UseBulkDeleteQuestionProvider } from '~teacher/components/QuizEditor/contexts/UseBulkDeleteQuestionProvider';
import { deleteTypeAtom } from '~teacher/components/QuizEditor/QuizEditor';
import { WorkspaceQuiz } from '~teacher/components/WorkspacesQuiz';
import { RouterProvider, TeacherRouter } from '~teacher/context/RouterContext';
import { workspaceKeys } from '~teacher/hook/workspaces/workspaceQueryKeyFactory';
import { queryClient } from '~teacher/queryClient';

import { QuizQuestionUrl } from '../../api/question';
import {
  deleteWorkspaceQuizUrl,
  updateWorkspaceQuestionOrderUrl,
  updateWorkspaceQuestionUrl,
  updateWorkspaceQuizUrl,
} from '../../api/workspaces/workspaceQuizEditorURLs';
import reduxStore from '../../store';

interface QuizEditorPageProps {
  workspaceId: string;
  workspaceQuizId: string;
}

function useOnSuccess(workspaceId: string, workspaceQuizId: string) {
  const [deleteType] = useAtom(deleteTypeAtom);
  const queryClient = useQueryClient();

  const handleOnSuccess = (_: unknown, { questionIds }) => {
    // Analytics
    new DeleteQuestionEventAdapter({
      source: 'shared_library',
      deleteType: deleteType,
      questionsCount: questionIds.length,
    }).sendAnalytics();

    return queryClient.invalidateQueries(
      workspaceKeys.byWorkspaceQuizId({
        workspaceId,
        workspaceQuizId: workspaceQuizId,
      }),
      { refetchType: 'all' }
    );
  };
  return handleOnSuccess;
}

export function QuizEditorPage({
  workspaceQuizId,
  workspaceId,
}: QuizEditorPageProps) {
  const baseQuizUrl = React.useMemo(
    () =>
      deleteWorkspaceQuizUrl({
        workspaceId,
        workspaceQuizId,
      }),
    [workspaceId, workspaceQuizId]
  );
  const quizUrl = React.useMemo(
    () =>
      updateWorkspaceQuizUrl({
        workspaceId,
        workspaceQuizId,
      }),
    [workspaceId, workspaceQuizId]
  );
  const quizQuestionUrl: QuizQuestionUrl = React.useMemo(
    () => updateWorkspaceQuestionUrl({ workspaceId, workspaceQuizId }),
    [workspaceQuizId, workspaceId]
  );
  const quizQuestionOrderUrl = React.useMemo(
    () => updateWorkspaceQuestionOrderUrl({ workspaceId, workspaceQuizId }),
    [workspaceQuizId, workspaceId]
  );
  const handleOnSuccess = useOnSuccess(workspaceId, workspaceQuizId);
  const bulkAddQuestions: ({ quizId }: { quizId: string }) => string = ({
    quizId,
  }: {
    quizId: string;
  }) =>
    updateWorkspaceQuestionUrl({
      workspaceId,
      workspaceQuizId,
      usePlural: true,
    })({
      quizId,
    });

  return (
    <DeleteQuizUrlContextProvider deleteQuizUrl={baseQuizUrl}>
      <QuizUrlContextProvider quizUrl={quizUrl}>
        <QuizQuestionUrlProvider quizQuestionUrl={quizQuestionUrl}>
          <QuizQuestionOrderUrlProvider
            quizQuestionOrderUrl={quizQuestionOrderUrl}
          >
            <BulkQuestionsUrlProvider bulkQuestionsUrl={bulkAddQuestions}>
              <UseBulkDeleteQuestionProvider
                key={workspaceId}
                createBody={(questionIds) => ({ question_ids: questionIds })}
                onSuccess={handleOnSuccess}
              >
                <WorkspaceQuiz
                  workspaceQuizId={workspaceQuizId}
                  workspaceId={workspaceId}
                />
              </UseBulkDeleteQuestionProvider>
            </BulkQuestionsUrlProvider>
          </QuizQuestionOrderUrlProvider>
        </QuizQuestionUrlProvider>
      </QuizUrlContextProvider>
    </DeleteQuizUrlContextProvider>
  );
}

interface DoWorkspaceQuizParams {
  workspaceId: string;
  workspaceQuizId: string;
  router: TeacherRouter;
}

export default function doWorkspaceQuiz({
  workspaceId,
  workspaceQuizId,
  router,
}: DoWorkspaceQuizParams) {
  ReactDOM.render(
    <Provider store={reduxStore}>
      <TeacherApplicationContextProvider
        applicationContext={TeacherApplicationContext.Socrative}
      >
        <RouterProvider router={router}>
          <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools initialIsOpen={false} />
            <QuizEditorPage
              workspaceQuizId={workspaceQuizId}
              workspaceId={workspaceId}
            />
          </QueryClientProvider>
        </RouterProvider>
      </TeacherApplicationContextProvider>
    </Provider>,
    document.getElementById('main-content')
  );
}
